import {Component, ElementRef, OnInit, ViewChild} from '@angular/core';
import {GlobalLoaderService} from '../../services/global-loader.service';
import {ApiAgentService, uuidv4} from '../../services/api-agent.service';
import {EnvironmentService} from '../../services/environment.service';
import {TranslateModule, TranslateService} from '@ngx-translate/core';
import {ActivatedRoute, RouterModule} from '@angular/router';
import {Product} from '../../models/product';
import {firstValueFrom} from 'rxjs';
import {FormsModule, ReactiveFormsModule} from '@angular/forms';
import {CommonModule} from '@angular/common';
import {SharePanelComponent} from '../share-panel/share-panel.component';

declare var $: any;
declare var Snackbar: any;

@Component({
  selector: 'app-products',
  templateUrl: './products.component.html',
  standalone: true,
  imports: [CommonModule, FormsModule, ReactiveFormsModule, TranslateModule, SharePanelComponent, RouterModule],
  styleUrls: ['./products.component.scss']
})
export class ProductsComponent implements OnInit {

  constructor(private loader: GlobalLoaderService,
              public env: EnvironmentService,
              private translator: TranslateService,
              private route: ActivatedRoute,
              private api: ApiAgentService) {
  }

  subAccountType: any;
  subAccountGovId: any;
  products: Product[];
  selectedProducts = [];
  onlineLabel: any;
  offlineLabel: any;
  loading: any;
  productShareLink: any;

  isAccountant: boolean;
  isManager: boolean;
  isSales: boolean;
  isMerchant: boolean;
  @ViewChild('bulkActionOptions') bulkActionOptions: ElementRef;

  public static compare(a: any, b: any) {
    if (a.id < b.id) {
      return -1;
    }
    if (a.id > b.id) {
      return 1;
    }
    return 0;
  }

  toggleSelectAll(event: Event): void {
    const isChecked = (event.target as HTMLInputElement).checked;
    this.products.forEach((product: Product) => (product.selected = isChecked));
  }

  async toggleSelectProduct(): Promise<void> {
    const selectedOptions = this.bulkActionOptions.nativeElement.value;
    if (selectedOptions === 'delete') {
      await this.deleteSelectedProducts();
    } else if (selectedOptions === 'online') {
      await this.turnOnSelectedProducts();
    } else if (selectedOptions === 'offline') {
      await this.turnOffSelectedProducts();
    }
    this.bulkActionOptions.nativeElement.value = '';
  }

  async deleteSelectedProducts(): Promise<void> {
    const selectedIds = this.products.filter((product) => product.selected).map((product) => product.id);
    if (selectedIds.length === 0) {
      Snackbar.show({
        text: await firstValueFrom(this.translator.get('No products selected')),
        pos: 'bottom-center'
      });
      return;
    }
    if (confirm(await firstValueFrom(this.translator.get('Are you sure you want to delete selected products?')))) {
      // Call API or service to delete products
      console.log('Deleting products:', selectedIds);
      for (let selectedId of selectedIds) {
        let deletedProduct: any = await this.api.postDeleteProduct(selectedId);
        if (deletedProduct && deletedProduct.id == selectedId) {

          let productLabel = await firstValueFrom(this.translator.get('The product'));

          let deleteText = await firstValueFrom(this.translator.get('successfully deleted'));
          Snackbar.show({
            text: productLabel + ' ' + selectedId + ' ' + deleteText,
            pos: 'bottom-center'
          });
        }
      }
      await this.loadData();
      this.bulkActionOptions.nativeElement.value = '';
    }
  }

  async turnOnSelectedProducts(): Promise<void> {
    const selectedIds = this.products.filter((product) => product.selected).map((product) => product.id);
    if (selectedIds.length === 0) {
      Snackbar.show({
        text: await firstValueFrom(this.translator.get('No products selected')),
        pos: 'bottom-center'
      });
      return;
    }
    if (confirm(await firstValueFrom(this.translator.get('Are you sure you want to turn on selected products?')))) {
      for (let selectedId of selectedIds) {
        let newProduct: any = await this.api.postProductLink(selectedId, uuidv4());
        if (newProduct && newProduct.id == selectedId) {
          let productLabel = await firstValueFrom(this.translator.get('The product'));
          let updateText = await firstValueFrom(this.translator.get('successfully updated'));
          Snackbar.show({
            text: productLabel + ' ' + selectedId + ' ' + updateText,
            pos: 'bottom-center'
          });
        }
      }
      await this.loadData();
      this.bulkActionOptions.nativeElement.value = '';
    }
  }

  async turnOffSelectedProducts(): Promise<void> {
    const selectedIds = this.products.filter((product) => product.selected).map((product) => product.id);
    if (selectedIds.length === 0) {
      Snackbar.show({
        text: await firstValueFrom(this.translator.get('No products selected')),
        pos: 'bottom-center'
      });
      return;
    }
    if (confirm(await firstValueFrom(this.translator.get('Are you sure you want to turn off selected products?')))) {
      for (let selectedId of selectedIds) {
        let newProduct: any = await this.api.postProductLink(selectedId, null);
        if (newProduct && newProduct.id == selectedId) {
          let productLabel = await firstValueFrom(this.translator.get('The product'));
          let updateText = await firstValueFrom(this.translator.get('successfully updated'));
          Snackbar.show({
            text: productLabel + ' ' + selectedId + ' ' + updateText,
            pos: 'bottom-center'
          });
        }
      }
      await this.loadData();
      this.bulkActionOptions.nativeElement.value = '';
    }
  }

  checkPermission(): any {
    this.route.queryParams.subscribe((params: any): void => {
      let perms = this.env.checkPermission(params['type'], params['govId']);
      this.isAccountant = perms.isAccountant;
      this.isSales = perms.isSales;
      this.isMerchant = perms.isMerchant;
      this.isManager = perms.isManager;
    });
  }

  async ngOnInit() {
    this.loading = true;
    this.onlineLabel = await firstValueFrom(this.translator.get('Online'));
    this.offlineLabel = await firstValueFrom(this.translator.get('Offline'));
    await this.loadResources();
    await this.loadData();
    this.loading = false;
    this.checkPermission();
  }

  async loadResources() {
    await this.loader.loadExternalStyles('/plugins/notification/snackbar/snackbar.min.css');
    await this.loader.loadExternalScript('/plugins/notification/snackbar/snackbar.min.js');
    setInterval(() => {
      $('.dropdown-toggle2').dropdown();
    }, 1000);
  }

  async deleteProduct(productId: any, productTitle: any) {
    let deletedProduct: any = await this.api.postDeleteProduct(productId);

    if (deletedProduct && deletedProduct.id == productId) {
      let productLabel = await firstValueFrom(this.translator.get('The product'));
      let deleteText = await firstValueFrom(this.translator.get('successfully deleted'));
      await this.loadData();
      Snackbar.show({
        text: productLabel + ' ' + productTitle + ' ' + deleteText,
        pos: 'bottom-center'
      });
    }
  }

  shareProduct(product) {
    this.productShareLink = this.env.getPaymentWebUrl() + '/pay/product/' + product.linkIdentity;
    $('#productShareLink').modal('show');
  }

  async changeOnlineStatus(productId: any, linkIdentity: any) {
    if (linkIdentity) {
      linkIdentity = null;
    } else {
      linkIdentity = uuidv4();
    }
    let newProduct: any = await this.api.postProductLink(productId, linkIdentity);
    console.log(newProduct);

    let index = this.products.findIndex(x => x.id === newProduct.id);
    this.products[index] = newProduct;
  }

  private async loadData() {
    this.products = await this.api.loadProducts();
    this.products.sort(ProductsComponent.compare);
  }

  async prepareDataTable() {
    await this.loader.loadExternalScript('/plugins/table/datatable/datatables.js');
    $('#multi-column-ordering').DataTable({
      'oLanguage': {
        'oPaginate': {
          'sPrevious': '<svg xmlns="http://www.w3.org/2000/svg" width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round" class="feather feather-arrow-right"><line x1="5" y1="12" x2="19" y2="12"></line><polyline points="12 5 19 12 12 19"></polyline></svg>',
          'sNext': '<svg xmlns="http://www.w3.org/2000/svg" width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round" class="feather feather-arrow-left"><line x1="19" y1="12" x2="5" y2="12"></line><polyline points="12 19 5 12 12 5"></polyline></svg>'
        },
        'sInfo': 'Showing page _PAGE_ of _PAGES_',
        'sSearch': '<svg xmlns="http://www.w3.org/2000/svg" width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round" class="feather feather-search"><circle cx="11" cy="11" r="8"></circle><line x1="21" y1="21" x2="16.65" y2="16.65"></line></svg>',
        'sSearchPlaceholder': 'Search...',
        'sLengthMenu': 'Results :  _MENU_',
      },
      'stripeClasses': [],
      'lengthMenu': [7, 10, 20, 50],
      'pageLength': 7,
      columnDefs: [{
        targets: [0],
        orderData: [0, 1]
      }, {
        targets: [1],
        orderData: [1, 0]
      }, {
        targets: [4],
        orderData: [4, 0]
      }]
    });
  }
}
